import React, { useState, useEffect } from "react";
import Select from 'react-select';
import axiosInstance from "../../utils/axiosInstance";

const TimeslotLimitsTable = ({
    selectedCompany, 
    timeslots,
    timeSlotRows,
    handleTimeslotSettingsChange,
    allowInboundBookings,
    allowOutboundBookings
}) =>{
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState(null);
    const [selectedTimeslotLimits, setSelectedTimeslotLimits] = useState([]); // Selected timeslot limits

    // Filter timeSlotRows to exclude disabled trip types
    // Values come from backend via Bookings.jsx, defaulting to true if not set
    const inboundAllowed = allowInboundBookings ?? true;
    const outboundAllowed = allowOutboundBookings ?? true;
    
    const filteredTimeSlotRows = timeSlotRows.filter(row => {
        if (row.trip_type === 'inbound') {
            return inboundAllowed;
        }
        if (row.trip_type === 'outbound') {
            return outboundAllowed;
        }
        // For null/undefined trip_type, include it (generic settings)
        return true;
    });

    const timeslotOptions = timeslots
        // Filter out timeslots with disabled trip types
        .filter(timeslot => {
            if (timeslot.trip_type === 'inbound' && !inboundAllowed) {
                return false;
            }
            if (timeslot.trip_type === 'outbound' && !outboundAllowed) {
                return false;
            }
            return true;
        })
        // Filter out timeslots that are already in the table or currently selected with the same trip_type
        .filter(timeslot => {
            // Check if timeslot is already in the table with the same timeslot and trip_type
            const isInTable = filteredTimeSlotRows.some(row => 
                row.timeslot === timeslot.timeslot && row.trip_type === timeslot.trip_type
            );
            
            // Check if timeslot is currently selected in the dropdown with the same timeslot and trip_type
            const isSelected = selectedTimeslotLimits.some(selectedOption => {
                // Parse composite value format: "timeslot|trip_type"
                const [selectedTimeslot, selectedTripType] = selectedOption.value.split('|');
                return selectedTimeslot === timeslot.timeslot && selectedTripType === timeslot.trip_type;
            });
            
            // Include timeslot only if it's not in the table and not currently selected
            return !isInTable && !isSelected;
        })
        .map(timeslot => ({
            value: `${timeslot.timeslot}|${timeslot.trip_type}`,
            label: `${timeslot.timeslot} (${timeslot.trip_type})`
        }));

    const calculateActiveTimeslot = (slot) => {
        return slot.company_admin_permission || slot.manager_permission || slot.employee_permission;
    };

    const handleTimeslotLimitsChange = (selectedOptions) => {
        setSelectedTimeslotLimits(selectedOptions || []);
    };

    // Add selected timeslot limits to the table
    const handleAddLimit = () => {
        const newRows = selectedTimeslotLimits.map((option) => {
            // Parse composite value format: "timeslot|trip_type"
            
            const [extractedTimeslot, extractedTripType] = option.value.split('|');
            
            // Double-check that the trip type is allowed (safety check)
            if (extractedTripType === 'inbound' && !inboundAllowed) {
                return null;
            }
            if (extractedTripType === 'outbound' && !outboundAllowed) {
                return null;
            }
            
            const slot = timeslots.find(slot => slot.timeslot === extractedTimeslot && slot.trip_type === extractedTripType) || {};

            slot.active_timeslot = calculateActiveTimeslot(slot);
            return {
                ...slot,
                timeslot: extractedTimeslot, // Ensure timeslot is set
                company_admin_permission: !!slot.company_admin_permission,
                manager_permission: !!slot.manager_permission,
                employee_permission: !!slot.employee_permission,
                active_timeslot: !!slot.active_timeslot,
                trip_type: extractedTripType,
            };
        }).filter(row => row !== null); // Remove any null entries from filtered trip types
        
        handleTimeslotSettingsChange([...timeSlotRows, ...newRows]);
        setSelectedTimeslotLimits([]);
    };

    // Handle timeslot limits role change
    const handleRoleChange = (index, role) => {
        // Find the original index in timeSlotRows
        const slot = filteredTimeSlotRows[index];
        const originalIndex = timeSlotRows.findIndex(row => 
            row.timeslot === slot.timeslot && 
            row.trip_type === slot.trip_type &&
            (row.id ? row.id === slot.id : true)
        );
        
        if (originalIndex < 0) return;
        
        const newTimeSlotRows = timeSlotRows.map((currentRow, i) => {
            if (i === originalIndex) {
                const updatedRow = {
                    ...currentRow,
                    [`${role}_permission`]: !currentRow[`${role}_permission`]
                };
                updatedRow.active_timeslot = calculateActiveTimeslot(updatedRow);
                return updatedRow;
            } else {
                return currentRow;
            }
        });
        handleTimeslotSettingsChange(newTimeSlotRows);
    };

    // Show delete confirmation dialog
    const handleDeleteClick = (index) => {
        setDeleteIndex(index);
        setShowDeleteConfirm(true);
    };

    // Confirm delete action
    const confirmDelete = async () => {
        if (deleteIndex === null) return;
        
        try {
            const rowToDelete = timeSlotRows[deleteIndex];
            
            // Set all permissions to false for the selected timeslot
            const payload = {
                company_admin_permission: false,
                manager_permission: false,
                employee_permission: false,
                active_timeslot: false,
                trip_type: rowToDelete.trip_type,
            };

            await axiosInstance.put(`/companies/${selectedCompany.id}/timeslot_settings/${rowToDelete.id}.json`, {
                timeslot_setting: payload
            });

            const updatedRows = timeSlotRows.filter((_, i) => i !== deleteIndex);
            handleTimeslotSettingsChange(updatedRows);
        } catch (error) {
            console.error('Error deleting timeslot limit:', error);
        } finally {
            setShowDeleteConfirm(false);
            setDeleteIndex(null);
        }
    };

    // Cancel delete action
    const cancelDelete = () => {
        setShowDeleteConfirm(false);
        setDeleteIndex(null);
    };

    return (
        <div>
            <label className="label font-medium text-gray-900 mt-8">Add Time Slot(s)</label>
            <div className="w-full flex flex-col space-y-4">
                <div className="w-full flex flex-row items-center space-x-4">
                    <div className="w-5/6">    
                        <Select
                            placeholder={'Select timeslot limits'}
                            isMulti
                            isSearchable
                            options={timeslotOptions}
                            onChange={handleTimeslotLimitsChange}
                            closeMenuOnSelect={false}
                            value={selectedTimeslotLimits}
                        />
                    </div>
                    <div className="w-1/6">
                        <button 
                            onClick={handleAddLimit} 
                            disabled={selectedTimeslotLimits.length === 0}
                            className={`button space-x-2 ${selectedTimeslotLimits.length === 0 ? 'cursor-not-allowed' : ''}`}
                        >
                            <i className="fas fa-plus" />
                            <span>Add Limit</span>
                        </button>
                    </div>
                </div>
            </div>
            <div className="w-full flex flex-row">
                <table className="table-auto w-full border-collapse">
                    <thead>
                        <tr>
                            <th className="px-4 py-2 text-left text-sm">Time Slot</th>
                            <th className="px-4 py-2 text-left text-sm">Trip Type</th>
                            <th className="px-4 py-2 text-left text-sm">User Role</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredTimeSlotRows.map((slot, index) => {
                            // Find the original index in timeSlotRows for proper deletion
                            const originalIndex = timeSlotRows.findIndex(row => 
                                row.timeslot === slot.timeslot && 
                                row.trip_type === slot.trip_type &&
                                (row.id ? row.id === slot.id : true)
                            );
                            return (
                            <tr key={slot.id || index}>
                                <td className="px-4 py-2 text-sm text-gray-700">{slot.timeslot}</td>
                                <td className="px-4 py-2 text-sm text-gray-700">
                                    {slot.trip_type === 'inbound' ? 'Inbound' : 
                                     slot.trip_type === 'outbound' ? 'Outbound' : 
                                     'All Trip Types'}
                                </td>
                                <td className="px-4 py-2 space-x-4 flex flex-row">
                                    <div className="flex flex-row pt-2">
                                        <div className="text-sm text-gray-700 mb-2 flex items-center">
                                            <input
                                                type="checkbox"
                                                className="w-6 h-6 text-sm text-gray-800 scale-125 mr-4"
                                                checked={!!slot.company_admin_permission}
                                                onChange={() => handleRoleChange(index, 'company_admin')}
                                            />   
                                        </div>
                                        <label className="text-sm text-gray-700 pt-1">Company Admin</label>
                                    </div>
                                    <div className="flex flex-row pt-2">
                                        <div className="text-sm text-gray-700 mb-2 flex items-center">
                                            <input
                                                type="checkbox"
                                                className="w-6 h-6 text-sm text-gray-800 scale-125 mr-4"
                                                checked={!!slot.manager_permission}
                                                onChange={() => handleRoleChange(index, 'manager')}
                                            />
                                        </div>
                                        <label className="text-sm text-gray-700 pt-1">Manager</label>
                                    </div>
                                    <div className="flex flex-row pt-2">
                                        <div className="text-sm text-gray-700 mb-2 flex items-center">
                                            <input
                                                type="checkbox"
                                                className="w-6 h-6 text-sm text-gray-800 scale-125 mr-4"
                                                checked={!!slot.employee_permission}
                                                onChange={() => handleRoleChange(index, 'employee')}
                                            />
                                        </div>
                                        <label className="text-sm text-gray-700 pt-1">Employee</label>
                                    </div>
                                </td>
                                <td>
                                    <button
                                        onClick={() => handleDeleteClick(originalIndex >= 0 ? originalIndex : index)}
                                        className="bg-red-600 text-white rounded font-bold text-sm items-center justify-center relative cursor-pointer"
                                        style={{ width: "40px", height: "45px" }}
                                    >
                                        <i className="fas fa-trash-alt"></i>
                                    </button>
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-lg">
                        <div className="flex mb-4">
                            <div >
                                <i className="fas fa-exclamation-triangle text-yellow-500 text-2xl"></i>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-lg font-medium text-gray-900">
                                    Confirm Deletion
                                </h3>
                            </div>
                        </div>
                        <div className="mb-6">
                            <p className="text-sm text-gray-500 whitespace-normal">
                                Are you sure you want to remove this timeslot limit? This action will disable all permissions for the timeslot "{deleteIndex !== null ? timeSlotRows[deleteIndex]?.timeslot : ''}".
                            </p>
                        </div>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={cancelDelete}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default TimeslotLimitsTable;